import { Button, Dialog, Flex, Heading, ModalOverlay, useDetectDeviceSize, type Action } from "@wayflyer/flyui";
import { AnimatePresence, domAnimation, LazyMotion, m, type AnimationProps } from "framer-motion";
import { useId, useRef } from "react";

import { DialogContent, DialogFooter, StyledModalPrimitive } from "./image-modal.styles";

export interface ImageModalProps {
  title: string;
  imageUrl: string;
  primaryAction?: Action;
  children?: React.ReactNode;
  isOpen: boolean;
  closeOnClickElseWhere?: boolean;
  onClose?(): void;
}

const MotionModalOverlay = m(ModalOverlay);
const MotionModalPrimitive = m(StyledModalPrimitive);
const transparent: AnimationProps["initial"] = { backgroundColor: "rgba(0, 0, 0, 0)" };
const opaque: AnimationProps["animate"] = {
  backgroundColor: "var(--palette-bg-neutral-overlay)",
  backdropFilter: "blur(var(--effects-blur))",
  opacity: 0.6,
};
const small: AnimationProps["initial"] = { transform: "scale(0.7)", opacity: 0 };
const fullSize: AnimationProps["animate"] = { transform: "scale(1)", opacity: 1 };

export const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  title,
  children,
  primaryAction,
  imageUrl,
  closeOnClickElseWhere = true,
  onClose,
}) => {
  const { isMobile } = useDetectDeviceSize();
  const scrollableElRef = useRef(null);
  const modalId = useId();

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {isOpen && (
          <MotionModalPrimitive
            isDismissable={closeOnClickElseWhere}
            shouldCloseOnInteractOutside={() => closeOnClickElseWhere}
            // @ts-expect-error - isOpen does not exist on ModalPrimitive
            isOpen={isOpen}
            onOpenChange={onClose}
            initial={small}
            animate={fullSize}
            exit={small}
            transition={{ duration: 0.3 }}
          >
            <MotionModalOverlay
              data-id="modal-mask"
              data-testid="modal-mask"
              initial={transparent}
              animate={opaque}
              exit={transparent}
              transition={{ duration: 0.6 }}
            />
            <Dialog role="dialog" aria-labelledby={modalId} style={{ maxHeight: "calc(100vh - 40px)" }}>
              <img src={imageUrl} alt={title} />
              <DialogContent direction="column" gap="8" padding={isMobile ? "4" : "6"}>
                <Flex direction="column" gap={isMobile ? "3" : "4"}>
                  <Heading size={isMobile ? "xl" : "2xl"}>{title}</Heading>
                  <div ref={scrollableElRef} data-testid="modal-body" data-id="modal-body">
                    {children}
                  </div>
                </Flex>
                {primaryAction && (
                  <DialogFooter>
                    <Button {...primaryAction} variant="Primary" fullWidth={isMobile} onClick={primaryAction.onAction}>
                      {primaryAction.content}
                    </Button>
                  </DialogFooter>
                )}
              </DialogContent>
            </Dialog>
          </MotionModalPrimitive>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
};
